const DEFAULT_LOCALE = 'ko-KR'

export function isValidDate(date: Date): boolean {
  return !Number.isNaN(date.getTime())
}

function normalizeLocale(locale?: string | null): string {
  const trimmed = locale?.trim()
  if (trimmed == 'cn') return 'zh-Hans' // 중국어 간체인 경우 > 'zh-hans'
  if (trimmed == 'zh') return 'zh-Hant' // 중국어 번체인 경우 > 'zh-hant'
  return trimmed && trimmed.length > 0 ? trimmed : DEFAULT_LOCALE
}

export function parseIsoDate(value?: string | null): Date | null {
  if (!value)
    return null

  const date = new Date(value)
  return isValidDate(date) ? date : null
}

export function parseYmdDate(value?: string | null): Date | null {
  if (!value)
    return null

  const [yearStr, monthStr, dayStr] = value.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const day = Number(dayStr)

  if ([year, month, day].some(number => Number.isNaN(number)))
    return null

  const date = new Date(year, month - 1, day)

  if (!isValidDate(date))
    return null

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day)
    return null

  return date
}

export function ensureYmdDate(value?: string | null, fallback?: Date): Date {
  return parseYmdDate(value) ?? new Date(fallback ?? Date.now())
}

export function formatYmd(date: Date, separator = '-'): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}${separator}${month}${separator}${day}`
}

export function formatDateLocalized(
  date: Date,
  locale?: string | null,
  options?: Intl.DateTimeFormatOptions,
): string {
  const formatter = new Intl.DateTimeFormat(
    normalizeLocale(locale),
    options ?? { year: 'numeric', month: 'long', day: 'numeric' },
  )
  return formatter.format(date)
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
}

export function formatDateRangeLocalized(
  start: Date | null | undefined,
  end: Date | null | undefined,
  locale?: string | null,
  options?: Intl.DateTimeFormatOptions,
  separator = ' ~ ',
): string {
  const normalizedLocale = normalizeLocale(locale)
  const formatter = new Intl.DateTimeFormat(
    normalizedLocale,
    options ?? { year: 'numeric', month: 'long', day: 'numeric' },
  )

  if (start && end) {
    if (isSameDay(start, end))
      return formatter.format(start)

    return `${formatter.format(start)}${separator}${formatter.format(end)}`
  }

  if (start)
    return formatter.format(start)

  if (end)
    return formatter.format(end)

  return ''
}

export function formatFullDateLocalized(date: Date, locale?: string | null): string {
  return formatDateLocalized(date, locale, { dateStyle: 'full', timeZone: 'Asia/Seoul' })
}

export function formatMonthRange(
  start: Date | null | undefined,
  end: Date | null | undefined,
): string {
  if (!start || !end)
    return ''

  return `${formatYmd(start, '. ')}. ~ ${formatYmd(end, '. ')}.`
}

/** 날짜를 특정 포멧으로 포멧팅 */
export function convertDate(date: Date | string, format?: string): string;
export function convertDate(date: Date | string | null | undefined, format?: string): string | null;
export function convertDate(date: Date | string | null | undefined, format: string = 'Y-m-d'): string | null {
    if (!date) return null;
    const dateObj = date instanceof Date ? date : new Date(date);
    // 날짜가 유효하지 않은 경우 처리
    if (isNaN(dateObj.getTime())) {
        throw new Error('Invalid date');
    }

    const year = dateObj.getFullYear().toString();
    const sYear = (dateObj.getFullYear() % 100).toString().padStart(2, '0');
    const sMonth = dateObj.getMonth() + 1;
    const month = sMonth.toString().padStart(2, '0');
    const sDay = dateObj.getDate();
    const day = sDay.toString().padStart(2, '0');
    const hours = dateObj.getHours().toString().padStart(2, '0');
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    const seconds = dateObj.getSeconds().toString().padStart(2, '0');
    const ms = dateObj.getMilliseconds().toString().padStart(3, '0');
    const weekName = ['일', '월', '화', '수', '목', '금', '토'][dateObj.getDay()]!;

    // format 문자열을 실제 날짜로 변환
    return format.replace('y', sYear)
      .replace('ms', ms)
      .replace('Y', year)
      .replace('m', month)
      .replace('d', day)
      .replace('H', hours)
      .replace('i', minutes)
      .replace('s', seconds)
      .replace('D', weekName)
      .replace('n', '' + sMonth)
      .replace('j', '' + sDay);
}
